// JoshSkills page (T-351, owner request 2026-11-03): the shadow trades the third-party day-trader-skill pack
// ("JoshSkills") would have made on our data, with an equity curve, a per-trade candle chart and the trade table,
// plus the skill catalogue (the former Skills page). READ-ONLY: data from joshskills/api.py (127.0.0.1:10010) via the
// '/api/joshskills' proxy; nothing here places an order. Native results use the pack's own fills (no costs); the
// "could have filled at" line is owner decision D3. Replay = past sessions, never counted toward the 4-week gate (D2).
// 2026-11-03 (owner): Time Machine panel per trade ("why cant I see it working?"), the REAL paper order at the JoshSkills
// Alpaca account beside each live shadow trade (T-353 executor; this page still sends nothing), and a FILINGS & NEWS
// tab (News headlines + 8-Ks, Analyst, House PTRs of all members, OpenInsider purchases).
// 2026-11-04 redesign (owner: "use the plugins that help the design and functionality ... including the Joshskills section",
// T-364): a status strip on top (broker orders on/halted, entry window, entries vs the executor's daily cap, today's real
// paper P&L vs the loss cap, fills-sync age, failed jobs), sentence-case tabs, chart marks from the palette validated
// with the dataviz skill (lib MARK), and a read-only Research capture tab (market_capture.py summary; not a signal).
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createChart, CrosshairMode, LineStyle } from 'lightweight-charts'
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts'
import { Panel, MONO } from '../components/deskParts'
import SkillLab from './SkillLab'
import { jsGet, money, rText, toCandles, tradeMarkers, statsLine, EXIT_TONE, VERDICT_TONE, MARK, windowState, todayLive, ageText, etParts } from '../lib/joshskills'

const btn = { ...MONO, fontSize: '0.64rem', padding: '3px 9px', borderRadius: 4, border: '1px solid #334155', color: '#cbd5e1', background: 'transparent', cursor: 'pointer' }
const on = { border: '1px solid #38bdf8', color: '#38bdf8' }   // full border, not borderColor: React warns when a shorthand and its part mix
const th = { textAlign: 'left', color: '#64748b', fontWeight: 400, padding: '2px 8px 2px 0', whiteSpace: 'nowrap' }
const td = { padding: '2px 8px 2px 0', color: '#cbd5e1', whiteSpace: 'nowrap' }
const verdictOf = (v) => { const s = (v || '').toLowerCase(); return s.includes('toward the target') ? 'toward target' : s.includes('toward the stop') ? 'toward stop' : (s.includes('coin flip') || s.includes('break-even')) ? 'coin flip' : 'unknown' }

const pct = (x) => (x == null ? '—' : `${Math.round(Number(x) * 100)}%`)

// What the Time Machine said at entry: 10,000 replays of the rest of the day from the stock's own past sessions, and
// the same rule on its past days. Most replays end at 15:55 with neither stop nor target hit, so the odds are shown as
// three parts that add to 100 %, not as a bare "target %" (which looked like 0 % and read as broken).
function TimeMachinePanel({ trade }) {
  const mc = trade.tm_mc || {}
  const h = trade.tm_history || {}
  const parts = [['target first', mc.p_target, MARK.target], ['stop first', mc.p_stop, MARK.stop], ['neither by 15:55', mc.p_neither, MARK.neither]]
  const decided = (Number(mc.p_target) || 0) + (Number(mc.p_stop) || 0)
  return (
    <div style={{ border: '1px solid #1e293b', borderRadius: 6, padding: 8, marginBottom: 8 }}>
      <div style={{ fontSize: '0.74rem', color: '#e2e8f0', marginBottom: 5 }}>Time Machine at entry <span style={{ color: '#64748b' }}>({mc.pool_sessions ?? '?'} past sessions of {trade.symbol}, data to {trade.tm_last_bar || '?'})</span></div>
      {mc.p_target == null ? <div style={{ fontSize: '0.72rem', color: '#64748b' }}>No Time Machine result stored for this trade.</div> : <>
        {/* dataviz: 2px surface gaps between segments, rounded ends, identity by swatch + label (never colour alone) */}
        <div role="img" aria-label={parts.map(([k, v]) => `${k} ${pct(v)}`).join(', ')} style={{ display: 'flex', gap: 2, height: 12, marginBottom: 5 }}>
          {parts.filter(([, v]) => Number(v) > 0).map(([k, v, c]) => <div key={k} title={`${k} ${pct(v)}`} style={{ width: `${(Number(v) || 0) * 100}%`, background: c, borderRadius: 3 }} />)}
        </div>
        <div style={{ ...MONO, fontSize: '0.7rem', color: '#cbd5e1' }}>
          {parts.map(([k, v, c]) => <span key={k} style={{ marginRight: 14, whiteSpace: 'nowrap' }}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: c, marginRight: 5 }} />{k} {pct(v)}</span>)}
          <span style={{ marginRight: 12 }}>ends green {pct(mc.p_green)}</span><span>avg {rText(mc.avg_R)}</span>
        </div>
        <div style={{ ...MONO, fontSize: '0.7rem', color: '#94a3b8', marginTop: 2 }}>
          of the replays that hit one: target first {decided ? pct(Number(mc.p_target) / decided) : '—'} · same rule on its past days: {h.triggered ?? '?'} trades, {h.target ?? 0} target / {h.stop ?? 0} stop / {h.close ?? 0} at the close, avg {rText(h.avg_R)}
        </div>
      </>}
      <div style={{ fontSize: '0.7rem', color: VERDICT_TONE[verdictOf(trade.tm_verdict)], marginTop: 4 }}>{trade.tm_verdict || '—'}</div>
      {trade.tm_note && <div style={{ fontSize: '0.66rem', color: '#64748b', marginTop: 2 }}>{trade.tm_note} Costs are ignored, so these odds flatter the trade. It never blocks a trade here.</div>}
    </div>
  )
}

// The real paper order at the JoshSkills Alpaca account for a live trade (T-353), beside the shadow.
function BrokerLine({ t }) {
  if (!t.broker_status) return null
  const tone = { closed: '#cbd5e1', open: '#38bdf8', working: '#fbbf24', unfilled: '#94a3b8', refused: '#f87171', rejected: '#f87171', error: '#f87171' }[t.broker_status] || '#94a3b8'
  return (
    <div style={{ ...MONO, fontSize: '0.7rem', color: tone, marginBottom: 4 }}>
      ALPACA PAPER (...13L7): {t.broker_status}{t.broker_reason ? ` (${t.broker_reason})` : ''}
      {t.broker_limit_price != null && ` · limit ${Number(t.broker_limit_price).toFixed(2)}`}
      {t.broker_entry_price != null && ` · filled ${Number(t.broker_qty)} @ ${Number(t.broker_entry_price).toFixed(2)} (shadow ${Number(t.entry_price).toFixed(2)})`}
      {t.broker_exit_price != null && ` · exit ${t.broker_exit_reason} @ ${Number(t.broker_exit_price).toFixed(2)} · ${money(t.broker_pnl)}`}
    </div>
  )
}

// One trade's day: 5-minute candles, entry / stop / target / exit lines and markers (New York wall time).
function TradeChart({ trade }) {
  const box = useRef(null)
  const [err, setErr] = useState(null)
  useEffect(() => {
    if (!trade || !box.current) return undefined
    const chart = createChart(box.current, {
      height: 320, layout: { background: { color: '#0f172a' }, textColor: '#64748b' },
      grid: { vertLines: { color: '#1e293b' }, horzLines: { color: '#1e293b' } }, crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#1e293b' }, timeScale: { borderColor: '#1e293b', timeVisible: true, secondsVisible: false },
    })
    const series = chart.addCandlestickSeries({ upColor: MARK.target, downColor: MARK.stop, borderVisible: false, wickUpColor: MARK.target, wickDownColor: MARK.stop })
    let alive = true
    jsGet(`/bars?symbol=${encodeURIComponent(trade.symbol)}&date=${String(trade.trade_date).slice(0, 10)}`).then(d => {
      if (!alive) return
      series.setData(toCandles(d.bars))
      const line = (price, color, title, style = LineStyle.Dashed) => price != null && series.createPriceLine({ price: Number(price), color, lineWidth: 1, lineStyle: style, axisLabelVisible: true, title })
      line(trade.entry_price, MARK.entry, 'entry', LineStyle.Solid)
      line(trade.stop, MARK.stop, 'stop')
      line(trade.target, MARK.target, 'target')
      if (trade.exit_price != null) line(trade.exit_price, EXIT_TONE[trade.exit_reason] || '#94a3b8', `exit ${trade.exit_reason}`, LineStyle.Dotted)
      series.setMarkers(tradeMarkers(trade))
      chart.timeScale().fitContent()
    }).catch(e => alive && setErr(e.message))
    return () => { alive = false; chart.remove() }
  }, [trade])
  if (!trade) return <div style={{ color: '#64748b', fontSize: '0.75rem' }}>Pick a trade in the table to see its chart.</div>
  return (
    <div>
      <div style={{ ...MONO, fontSize: '0.72rem', color: '#cbd5e1', marginBottom: 4 }}>
        {trade.symbol} {String(trade.trade_date).slice(0, 10)} · buy {Number(trade.qty)} @ {Number(trade.entry_price).toFixed(2)} · stop {Number(trade.stop).toFixed(2)} · target {Number(trade.target).toFixed(2)} ({trade.target_r}R) ·
        {' '}<span style={{ color: EXIT_TONE[trade.exit_reason] || '#94a3b8' }}>{trade.status === 'open' ? 'OPEN' : `${trade.exit_reason} @ ${Number(trade.exit_price).toFixed(2)} ${rText(trade.r_multiple)} ${money(trade.pnl)}`}</span>
      </div>
      <BrokerLine t={trade} />
      <TimeMachinePanel trade={trade} />
      {err && <div style={{ color: '#f87171', fontSize: '0.72rem' }}>chart data error: {err}</div>}
      <div ref={box} />
    </div>
  )
}

function ShadowView() {
  const [mode, setMode] = useState('replay')
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [pick, setPick] = useState(null)
  useEffect(() => {
    let alive = true
    setData(null); setErr(null); setPick(null)
    const load = () => jsGet(`/overview?mode=${mode}`).then(d => { if (alive) { setData(d); setErr(null) } }).catch(e => alive && setErr(e.message))
    load()
    const id = setInterval(load, 60000)
    return () => { alive = false; clearInterval(id) }
  }, [mode])
  const trades = useMemo(() => [...(data?.trades || [])].reverse(), [data])
  // open on the newest trade so its chart and Time Machine panel are visible without a click (owner 2026-11-03)
  useEffect(() => { if (!pick && trades.length) setPick(trades[0]) }, [trades, pick])
  const s = data?.stats
  const failed = data?.failed_24h || []
  return (
    <div className="space-y-3">
      <Panel title="JoshSkills shadow trades" right={<span className="flex gap-2">
        {['replay', 'live'].map(m => <button key={m} style={{ ...btn, ...(mode === m ? on : {}) }} onClick={() => setMode(m)}>{m === 'replay' ? 'Replay' : 'Live'}</button>)}
      </span>}>
        {err && <div style={{ color: '#f87171', fontSize: '0.75rem' }}>JoshSkills API not reachable: {err}. Is joshskills-api running on 127.0.0.1:10010?</div>}
        {failed.length > 0 && <div style={{ color: '#f87171', fontSize: '0.75rem' }}>{failed.length} failed JoshSkills job run(s) in the last 24 h: {failed.map(f => `${f.job} ${String(f.started_at).slice(0, 16)}`).join(', ')}</div>}
        <div style={{ ...MONO, fontSize: '0.75rem', color: '#cbd5e1' }}>{data ? statsLine(s) : (err ? '' : 'loading…')}</div>
        {s && s.closed > 0 && <div style={{ ...MONO, fontSize: '0.7rem', color: '#94a3b8', marginTop: 4 }}>
          exits: target {s.by_exit.target} · stop {s.by_exit.stop} · flat 15:55 {s.by_exit.flat_by + (s.by_exit.flat_lastbar || 0)} · open {s.open}
        </div>}
        {data?.executor?.halted && <div style={{ color: '#f87171', fontSize: '0.75rem', marginTop: 4 }}>Broker orders HALTED: {data.executor.reason} (delete /srv/joshskills/state/executor.off to resume)</div>}
        {mode === 'live' && s?.broker && <div style={{ ...MONO, fontSize: '0.7rem', color: '#38bdf8', marginTop: 4 }}>
          Alpaca paper (...13L7): {s.broker.sent} sent · {s.broker.filled} filled · {s.broker.closed} closed · real {money(s.broker.pnl)} vs shadow {money(s.broker.shadow_pnl_same_trades)} on the same trades
          {s.broker.avg_entry_slip != null && ` · entry slippage ${s.broker.avg_entry_slip >= 0 ? '+' : ''}${s.broker.avg_entry_slip.toFixed(3)}/share (${money(-s.broker.entry_slip_usd)})`}
          {Object.keys(s.broker.by_status || {}).length > 0 && ` · ${Object.entries(s.broker.by_status).map(([k, n]) => `${k} ${n}`).join(', ')}`}
        </div>}
        <div style={{ fontSize: '0.7rem', color: '#fbbf24', marginTop: 6 }}>
          {data?.labels?.native}. {mode === 'replay' ? data?.labels?.replay + '.' : 'Live: each risk PASS is also sent to the JoshSkills Alpaca PAPER account as a bracket order (owner turn-on 2026-11-03); the real fills are the evidence, the shadow is the baseline.'} Not evidence of edge.
        </div>
      </Panel>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
        <Panel title="Equity curve (shadow account, $200,000 start)">
          {data?.equity?.length ? (
            <div style={{ height: 260 }}>
              <ResponsiveContainer>
                <LineChart data={data.equity} margin={{ top: 8, right: 12, left: 8, bottom: 0 }}>
                  <CartesianGrid stroke="#1e293b" vertical={false} />
                  <XAxis dataKey="date" stroke="#64748b" fontSize={10} />
                  {/* T-364 audit: $ axis and tooltip (raw 200200 was unreadable); linear lines, because monotone smoothing
                      drew peaks between daily closes that never happened (dataviz anti-pattern) */}
                  <YAxis stroke="#64748b" fontSize={10} domain={['auto', 'auto']} width={70} tickFormatter={v => '$' + Math.round(v).toLocaleString('en-US')} />
                  <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', fontSize: 11 }} formatter={v => '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="linear" dataKey="equity" name="native" stroke={MARK.native} strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="linear" dataKey="equity_alt" name="could have filled at" stroke={MARK.alt} strokeWidth={2} strokeDasharray="5 4" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>) : <div style={{ color: '#64748b', fontSize: '0.75rem' }}>no closed trades yet</div>}
        </Panel>
        <Panel title="By Time Machine verdict at entry">
          <table style={{ ...MONO, fontSize: '0.72rem', width: '100%' }}>
            <thead><tr><th style={th}>verdict</th><th style={th}>trades</th><th style={th}>won</th><th style={th}>avg R</th><th style={th}>P&L</th></tr></thead>
            <tbody>{Object.entries(s?.by_verdict || {}).map(([k, g]) => (
              <tr key={k}><td style={{ ...td, color: VERDICT_TONE[k] }}>{k}</td><td style={td}>{g.n}</td><td style={td}>{g.wins}</td><td style={td}>{rText(g.avg_r)}</td><td style={td}>{money(g.pnl)}</td></tr>))}</tbody>
          </table>
          <div style={{ fontSize: '0.68rem', color: '#64748b', marginTop: 6 }}>Every risk PASS is taken whatever the verdict, so this shows whether the Time Machine's warnings carried information. Small samples are noise.</div>
        </Panel>
      </div>
      <Panel title="Trade chart"><TradeChart trade={pick} /></Panel>
      <Panel title={`Trades (${trades.length})`}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...MONO, fontSize: '0.72rem', width: '100%' }}>
            <thead><tr>{['date', 'symbol', 'entry', 'qty', 'stop', 'target', 'exit', 'reason', 'R', 'P&L', 'could-have P&L', 'TM target / stop / neither', 'Alpaca', 'Alpaca P&L'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{trades.map(t => (
              <tr key={t.id} onClick={() => setPick(t)} style={{ cursor: 'pointer', background: pick?.id === t.id ? '#1e293b' : 'transparent' }}>
                <td style={td}>{String(t.trade_date).slice(0, 10)}</td><td style={td}>{t.symbol}</td>
                <td style={td}>{Number(t.entry_price).toFixed(2)}</td><td style={td}>{Number(t.qty)}</td>
                <td style={td}>{Number(t.stop).toFixed(2)}</td><td style={td}>{Number(t.target).toFixed(2)}</td>
                <td style={td}>{t.exit_price != null ? Number(t.exit_price).toFixed(2) : 'open'}</td>
                <td style={{ ...td, color: EXIT_TONE[t.exit_reason] || '#94a3b8' }}>{t.exit_reason || '—'}</td>
                <td style={td}>{rText(t.r_multiple)}</td><td style={{ ...td, color: Number(t.pnl) >= 0 ? '#34d399' : '#f87171' }}>{money(t.pnl)}</td>
                <td style={td}>{money(t.alt_pnl)}</td>
                <td style={{ ...td, color: VERDICT_TONE[verdictOf(t.tm_verdict)] }}>{t.tm_p_target != null ? `${pct(t.tm_p_target)} / ${pct(t.tm_p_stop)} / ${pct(t.tm_mc?.p_neither)}` : '—'}</td>
                <td style={td}>{t.broker_status ? (t.broker_entry_price != null ? `${t.broker_status} @ ${Number(t.broker_entry_price).toFixed(2)}` : t.broker_status) : '—'}</td>
                <td style={{ ...td, color: Number(t.broker_pnl) >= 0 ? '#34d399' : '#f87171' }}>{t.broker_pnl != null ? money(t.broker_pnl) : '—'}</td>
              </tr>))}</tbody>
          </table>
        </div>
      </Panel>
    </div>
  )
}

// New York wall time "MM-DD HH:MM ET" (the feed stores UTC; House filings carry a date only)
const etTime = (x) => {
  if (!x) return '—'
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(x))) return String(x)
  const d = new Date(x)
  return isNaN(d) ? String(x) : d.toLocaleString('en-US', { timeZone: 'America/New_York', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) + ' ET'
}
const FEED_KINDS = [['', 'all'], ['insider', 'insider buys'], ['congress', 'congress'], ['news', 'news + 8-K'], ['analyst', 'analyst']]

// FILINGS & NEWS: what the News, Analyst, Congress (all House PTRs) and OpenInsider jobs stored (read-only).
function FeedView() {
  const [kind, setKind] = useState('')
  const [onlyFlagged, setOnlyFlagged] = useState(false)
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  useEffect(() => {
    let alive = true
    jsGet(`/feed?limit=300${kind ? `&kind=${kind}` : ''}`).then(d => alive && (setData(d), setErr(null))).catch(e => alive && setErr(e.message))
    return () => { alive = false }
  }, [kind])
  const items = (data?.items || []).filter(i => !onlyFlagged || i.flagged)
  return (
    <div className="space-y-3">
      <Panel title="Filings & news" right={<span className="flex gap-2">
        {FEED_KINDS.map(([k, label]) => <button key={k} style={{ ...btn, ...(kind === k ? on : {}) }} onClick={() => setKind(k)}>{label}</button>)}
        <button style={{ ...btn, ...(onlyFlagged ? on : {}) }} onClick={() => setOnlyFlagged(!onlyFlagged)}>flagged only</button>
      </span>}>
        {err && <div style={{ color: '#f87171', fontSize: '0.75rem' }}>JoshSkills API not reachable: {err}</div>}
        <div style={{ ...MONO, fontSize: '0.7rem', color: '#94a3b8', marginBottom: 6 }}>
          {(data?.counts || []).map(c => `${c.kind} ${c.n} (${c.flagged} flagged)`).join(' · ') || 'nothing stored yet'}
          {(data?.jobs || []).map(j => <span key={j.job} style={{ marginLeft: 10, color: j.status === 'OK' ? '#64748b' : '#f87171' }}>{j.job} {j.status} {String(j.started_at).slice(5, 16)}</span>)}
        </div>
        <div style={{ fontSize: '0.68rem', color: '#64748b', marginBottom: 6 }}>
          Insider buys: OpenInsider (SEC Form 4, filed within 2 business days), flagged by fixed rules: 3+ insiders and $250k+, a CEO/CFO/President buying $250k+, an officer or director adding 20 %+ worth $100k+, or a stock in today's scan. Congress: every House member's new PTR (up to 45 days late by law), flagged for watched members or a range above $250k. Leads, not signals; nothing here trades.
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...MONO, fontSize: '0.72rem', width: '100%' }}>
            <thead><tr>{['when', 'kind', 'symbol', 'item'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{items.map(i => (
              <tr key={i.id} style={{ background: i.flagged ? '#1e293b' : 'transparent' }}>
                <td style={td}>{etTime(i.published_at || i.created_at)}</td>
                <td style={td}>{i.kind}</td><td style={td}>{i.symbol || '—'}</td>
                <td style={{ ...td, whiteSpace: 'normal', color: i.flagged ? '#fbbf24' : '#cbd5e1' }}>{i.url ? <a href={i.url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{i.title}</a> : i.title}</td>
              </tr>))}</tbody>
          </table>
        </div>
      </Panel>
    </div>
  )
}

// ---- Status strip (T-364): one row of instrument readouts, each against the limit the executor enforces ----------
// Reads overview?mode=live (today's real paper orders) every 30 s; limits come from joshskills/executor.py LIMITS via
// the API (executor_state), so the gauges can never disagree with the code that stops trading.
function Readout({ label, value, sub, tone = '#e2e8f0', children }) {
  return (
    // T-364 [LIVE 2026-11-04 shot @1366]: content-sized cells pushed the 6th readout onto a second row; flex 1 1 150px
    // shares one row on desktop and still wraps evenly on narrow screens.
    <div style={{ padding: '8px 14px', borderRight: '1px solid #1e293b', flex: '1 1 150px', minWidth: 0 }}>
      <div style={{ fontSize: '0.68rem', color: '#64748b', marginBottom: 2 }}>{label}</div>
      <div style={{ fontFamily: 'Rajdhani, sans-serif', fontWeight: 600, fontSize: '1.35rem', lineHeight: 1.1, color: tone }}>{value}</div>
      {children}
      {sub && <div style={{ fontSize: '0.66rem', color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

function StatusStrip() {
  const [d, setD] = useState(null)
  const [err, setErr] = useState(null)
  const [now, setNow] = useState(new Date())
  useEffect(() => {
    let alive = true
    const load = () => jsGet('/overview?mode=live').then(x => { if (alive) { setD(x); setErr(null); setNow(new Date()) } }).catch(e => alive && setErr(e.message))
    load()
    const id = setInterval(load, 30000)
    return () => { alive = false; clearInterval(id) }
  }, [])
  if (err) return <div role="alert" style={{ border: '1px solid #7f1d1d', borderRadius: 6, padding: '8px 12px', color: '#fca5a5', fontSize: '0.78rem' }}>The JoshSkills API did not answer ({err}). Check that joshskills-api is running on 127.0.0.1:10010; nothing below is current until it does.</div>
  if (!d) return <div style={{ color: '#64748b', fontSize: '0.75rem', padding: 8 }}>Loading today's status…</div>
  const ex = d.executor || {}
  const L = ex.limits || {}
  const t = todayLive(d.trades, now)
  const w = windowState(L, now)
  const fills = (d.jobs || []).find(j => j.job === 'fills')
  const lossUsed = Math.min(1, Math.max(0, -t.pnl / (L.max_daily_loss || 1)))
  const cap = L.max_entries_per_day || 0
  const orders = ex.halted ? ['Halted', '#f87171', ex.reason || 'kill switch file present'] : ex.enabled ? ['Sending', '#22d3ee', 'each risk pass goes to Alpaca paper ...13L7'] : ['Off', '#94a3b8', 'executor disabled in code']
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', background: '#0d1424', border: '1px solid #1e293b', borderRadius: 6 }}>
      <Readout label="Broker orders" value={orders[0]} tone={orders[1]} sub={orders[2]} />
      <Readout label={`Entry window ${L.entry_start || '?'}–${L.entry_end || '?'} ET`} value={{ open: 'Open', before: 'Not yet', after: 'Closed', unknown: '—' }[w]}
        tone={w === 'open' ? '#22d3ee' : '#94a3b8'} sub={w === 'before' ? `opens ${L.entry_start} ET (now ${etParts(now).hm})` : w === 'after' ? `open positions flatten at ${L.flatten_at} ET` : `now ${etParts(now).hm} ET`} />
      <Readout label="Entries today" value={`${t.sent} of ${cap || '?'}`} sub={`${t.filled} filled, ${t.open} open, ${t.closed} closed`}>
        {/* one cell per allowed entry: filled cells = orders sent today */}
        <div aria-hidden style={{ display: 'flex', gap: 2, marginTop: 4 }}>
          {Array.from({ length: cap }, (_, k) => <span key={k} style={{ width: 9, height: 6, borderRadius: 1, background: k < t.sent ? '#22d3ee' : '#1e293b' }} />)}
        </div>
      </Readout>
      <Readout label="Real paper P&L today" value={money(t.pnl)} tone={t.pnl < 0 ? '#f87171' : t.pnl > 0 ? '#34d399' : '#e2e8f0'}
        sub={`daily loss cap -$${L.max_daily_loss ?? '?'}: ${Math.round(lossUsed * 100)}% used`}>
        <div aria-hidden style={{ height: 5, background: '#1e293b', borderRadius: 3, marginTop: 4, width: 120 }}>
          <div style={{ height: 5, width: `${lossUsed * 100}%`, background: lossUsed > 0.8 ? '#f87171' : MARK.alt, borderRadius: 3 }} />
        </div>
      </Readout>
      <Readout label="Fills sync" value={fills ? fills.status : 'no run'} tone={fills && fills.status === 'OK' ? '#e2e8f0' : '#fbbf24'} sub={fills ? ageText(fills.started_at, now) : 'the fills job has not run yet'} />
      <Readout label="Failed jobs, 24 h" value={(d.failed_24h || []).length} tone={(d.failed_24h || []).length ? '#f87171' : '#e2e8f0'}
        sub={(d.failed_24h || []).slice(0, 2).map(f => f.job).join(', ') || 'none'} />
    </div>
  )
}

// ---- Research capture (T-364): what market_capture.py read on the cataloged sites; research inputs, not a signal ----
function ResearchView() {
  const [d, setD] = useState(null)
  const [err, setErr] = useState(null)
  useEffect(() => { jsGet('/research').then(setD).catch(e => setErr(e.message)) }, [])
  if (err) return <Panel title="Research capture"><div role="alert" style={{ color: '#f87171', fontSize: '0.75rem' }}>Could not load the capture summary: {err}</div></Panel>
  if (!d) return <Panel title="Research capture"><div style={{ color: '#64748b', fontSize: '0.75rem' }}>Loading…</div></Panel>
  const s = d.summary
  if (!s) return <Panel title="Research capture"><div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>No capture yet. Run <code>scripts/ops/browser/market_capture.py</code>; its summary appears here.</div></Panel>
  const maxSites = Math.max(1, ...s.top_tickers.map(t => t.n_sites))
  return (
    <div className="space-y-3">
      <Panel title={`Research capture, ${d.day}`}>
        <div style={{ fontSize: '0.78rem', color: '#e2e8f0' }}>{s.sites} sites read, {s.items} items: {s.by_kind.ticker} about tickers, {s.by_kind.macro} macro, {s.by_kind.headline} headlines.</div>
        <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 4 }}>
          Sources of each item: {Object.entries(s.by_basis || {}).map(([k, n]) => `${k} ${n}`).join(', ')}. "model, verified" means the local model copied it and the script found the same words on the page; the others are the script's own patterns or the site's RSS feed.
        </div>
        <div style={{ fontSize: '0.7rem', color: '#fbbf24', marginTop: 4 }}>Research inputs for a future ticker and market score. Not a score, not a signal; nothing here trades.</div>
      </Panel>
      <div className="grid gap-3" style={{ gridTemplateColumns: 'minmax(0,3fr) minmax(0,2fr)' }}>
        <Panel title="Tickers seen on the most sites">
          <table style={{ ...MONO, fontSize: '0.72rem', width: '100%' }}>
            <thead><tr>{['ticker', 'sites', 'items', 'moves seen', 'example'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{s.top_tickers.slice(0, 30).map(t => (
              <tr key={t.ticker}>
                <td style={td} title={t.name}>{t.ticker}</td>
                <td style={td}><span style={{ display: 'inline-block', width: 40 * t.n_sites / maxSites, height: 6, background: MARK.native, borderRadius: 2, marginRight: 6 }} />{t.n_sites}</td>
                <td style={td}>{t.items}</td>
                <td style={td}>{(t.moves || []).slice(0, 3).join(', ') || '—'}</td>
                <td style={{ ...td, whiteSpace: 'normal', color: '#94a3b8', fontFamily: 'DM Sans, sans-serif' }}>{t.examples?.[0] ? <a href={t.examples[0].url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>{t.examples[0].site}: {t.examples[0].text.slice(0, 120)}</a> : '—'}</td>
              </tr>))}</tbody>
          </table>
          {!s.top_tickers.length && <div style={{ color: '#64748b', fontSize: '0.75rem' }}>No tickers found yet.</div>}
        </Panel>
        <Panel title="Macro and market items">
          {Object.entries(s.macro || {}).map(([k, v]) => (
            <div key={k} style={{ marginBottom: 8 }}>
              <div style={{ fontSize: '0.76rem', color: '#e2e8f0' }}>{k} <span style={{ color: '#64748b' }}>({v.length})</span></div>
              {v.slice(0, 3).map((x, j) => <div key={j} style={{ fontSize: '0.7rem', color: '#94a3b8', paddingLeft: 8 }}>{x.site}: {x.text.slice(0, 140)}</div>)}
            </div>))}
          {!Object.keys(s.macro || {}).length && <div style={{ color: '#64748b', fontSize: '0.75rem' }}>No macro items found yet.</div>}
        </Panel>
      </div>
      <Panel title="Sites">
        <table style={{ ...MONO, fontSize: '0.72rem', width: '100%' }}>
          <thead><tr>{['site', 'status', 'pages', 'items', 'note'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>{(s.site_status || []).map(r => (
            <tr key={r.site}><td style={td}>{r.site}</td><td style={{ ...td, color: r.status === 'ok' ? '#cbd5e1' : '#fbbf24' }}>{r.status}</td><td style={td}>{r.pages ?? '—'}</td><td style={td}>{r.items ?? '—'}</td><td style={{ ...td, whiteSpace: 'normal', color: '#64748b' }}>{r.note || ''}</td></tr>))}</tbody>
        </table>
      </Panel>
    </div>
  )
}

const TABS = [['shadow', 'Trades'], ['research', 'Research capture'], ['feed', 'Filings & news'], ['catalog', 'Skill catalogue']]
const tabStyle = (active) => ({ fontSize: '0.8rem', padding: '6px 2px', marginRight: 18, background: 'transparent', border: 'none', borderBottom: `2px solid ${active ? '#22d3ee' : 'transparent'}`, color: active ? '#e2e8f0' : '#94a3b8', cursor: 'pointer' })

export default function JoshSkills() {
  const [tab, setTab] = useState('shadow')
  return (
    <div className="p-3 space-y-3">
      <StatusStrip />
      <div role="tablist" aria-label="JoshSkills sections" style={{ borderBottom: '1px solid #1e293b' }}>
        {TABS.map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} className="focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-400" style={tabStyle(tab === k)} onClick={() => setTab(k)}>{label}</button>))}
      </div>
      {tab === 'shadow' ? <ShadowView /> : tab === 'research' ? <ResearchView /> : tab === 'feed' ? <FeedView /> : <SkillLab />}
    </div>
  )
}
